// src/pages/InflationCalculator.tsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Simple synthetic YoY CPI series (replace with real BLS if you like)
function buildYears(start: number, end: number) {
  const out: number[] = [];
  for (let y = start; y <= end; y++) out.push(y);
  return out;
}
function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

export default function InflationCalculator() {
  const thisYear = new Date().getFullYear();
  const [rangeStart, setRangeStart] = useState(thisYear - 30);
  const [rangeEnd, setRangeEnd] = useState(thisYear);
  const [amount, setAmount] = useState("1000");
  const [annualInflation, setAnnualInflation] = useState("2.5"); // used for projection math only

  const years = useMemo(() => {
    const s = clamp(rangeStart, 1913, thisYear);
    const e = clamp(rangeEnd, s, thisYear);
    return buildYears(s, e);
  }, [rangeStart, rangeEnd, thisYear]);

  // Make a plausible YoY CPI series (flat-ish with noise).
  const chartData = useMemo(() => {
    const out = [];
    for (const y of years) {
      // light synthetic shaping: higher in early 80s / 2022, otherwise 1–3%
      let base = 2 + Math.sin((y - 1990) / 4) * 0.5;
      if (y === 2022) base = 7.0;
      if (y === 2023) base = 4.0;
      if (y === 2024) base = 3.1;
      out.push({ year: y, yoy: +base.toFixed(1) });
    }
    return out;
  }, [years]);

  // Projection calculator: “What will $X be in N years with inflation r?”
  const amt = +amount || 0;
  const r = (+annualInflation || 0) / 100;
  const nYears = Math.max(0, (Math.max(rangeEnd, thisYear) - thisYear));
  const projected = amt * Math.pow(1 + r, nYears);
  const realPower = projected ? (amt / projected) * amt : amt;

  const canonical = "https://free1099calc.com/calculators/inflation";

  return (
    <main className="mx-auto max-w-6xl px-4 md:px-6 py-8">
      <Helmet>
        <title>Free Inflation Calculator - CPI & Purchasing Power Analysis | Free1099Calc</title>
        <meta
          name="description"
          content="Calculate inflation impact on purchasing power with US CPI data. Adjust date ranges, visualize year-over-year inflation trends, and estimate future dollar values for freelancers and contractors."
        />
        <meta name="keywords" content="inflation calculator, CPI calculator, purchasing power, inflation rate, US inflation, consumer price index, cost of living adjustment, COLA calculator, inflation impact" />
        <link rel="canonical" href={canonical} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Inflation Calculator - CPI & Purchasing Power" />
        <meta property="og:description" content="Track US inflation trends and calculate how rising prices impact your purchasing power over time." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/calculators/inflation" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Inflation Calculator - CPI Analysis" />
        <meta name="twitter:description" content="Visualize inflation trends and calculate purchasing power changes with CPI data." />
      </Helmet>

      {/* Hero */}
      <section className="mb-10 rounded-3xl border-2 border-gray-200 bg-gradient-to-br from-purple-600 via-pink-700 to-rose-900 p-10 md:p-14 shadow-2xl text-center">
        <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-5 py-2 mb-6">
          <svg className="w-5 h-5 text-white mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-bold text-white">TRACK YOUR PURCHASING POWER</span>
        </div>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-6 leading-tight">Inflation Calculator</h1>
        <p className="text-xl md:text-2xl text-purple-100 max-w-4xl mx-auto leading-relaxed">
          Explore year-over-year US inflation and see how rising prices impact a number you care about.
          Adjust the date range to view more history.
        </p>
      </section>

      {/* Expense Tracker Banner */}
      <div className="mb-8">
        <ExpenseTrackerBanner compact />
      </div>

      {/* Controls */}
      <section className="rounded-3xl border-2 border-purple-200 bg-white p-8 md:p-10 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-200 rounded-xl">
            <svg className="w-7 h-7 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Your Inputs</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <NumberField
            label={`Range start (1913–${thisYear})`}
            value={rangeStart}
            onChange={(n) => setRangeStart(clamp(n, 1913, thisYear))}
          />
          <NumberField
            label={`Range end (start–${thisYear})`}
            value={rangeEnd}
            onChange={(n) => setRangeEnd(clamp(n, rangeStart, thisYear))}
          />
          <PercentField
            label="Assumed annual inflation for projection (%)"
            value={annualInflation}
            onChange={setAnnualInflation}
          />
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-6">
          <MoneyField label="Amount to project (today's $)" value={amount} onChange={setAmount} />
          <DisplayField label="Years projected" value={String(nYears)} />
          <DisplayField
            label="Projected nominal amount"
            value={
              (projected || 0).toLocaleString(undefined, {
                style: "currency",
                currency: "USD",
                maximumFractionDigits: 0,
              }) as unknown as string
            }
          />
        </div>

        <p className="mt-2 text-xs text-slate-600">
          Projection uses your assumed annual inflation rate from the current year through the end of your selected range.
        </p>
      </section>

      {/* Chart */}
      <section className="mt-6 rounded-2xl border bg-white p-4 md:p-6">
        <div className="mb-3">
          <h2 className="text-xl font-semibold">Year-over-year inflation (YoY)</h2>
          <p className="text-sm text-slate-600">
            Synthetic series for demo. Swap in BLS CPI YoY for production (BLS CPI-U).
          </p>
        </div>
        <div className="h-80 w-full rounded-2xl border-2 border-purple-100 bg-white p-6 shadow-lg">
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v: any) => `${v}%`} />
              <Line type="monotone" dataKey="yoy" name="YoY CPI (%)" dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* How it works */}
      <section className="mt-10 rounded-3xl border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-rose-50 p-8 md:p-10 shadow-xl">
        <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-pink-600 to-rose-600 rounded-lg">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
          </div>
          Understanding Inflation and Your Purchasing Power
        </h2>
        
        <div className="space-y-8 text-gray-700">
          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">What Is Inflation?</h3>
            <p className="mb-4">
              <strong>Inflation</strong> measures how much the general level of prices for goods and services rises over time, 
              reducing the purchasing power of your money. When inflation is 3%, something that costs $100 today will cost 
              approximately $103 next year. For freelancers and contractors, understanding inflation is crucial for pricing 
              your services, planning retirement, and negotiating long-term contracts.
            </p>
            <p>
              The most common measure is the <strong>Consumer Price Index (CPI)</strong>, published monthly by the U.S. 
              Bureau of Labor Statistics. CPI tracks the average change in prices paid by urban consumers for a basket of 
              goods and services including food, housing, transportation, healthcare, and entertainment.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Why Freelancers Should Care About Inflation</h3>
            <p className="mb-3">As a 1099 worker or independent contractor, inflation affects you differently than traditional employees:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Rate adjustments:</strong> You need to increase your rates periodically just to maintain the same purchasing power. If inflation averages 3% annually and you don't raise rates, you're effectively taking a 3% pay cut each year.</li>
              <li><strong>Long-term contracts:</strong> Fixed-price contracts that span multiple years lose value due to inflation. Consider inflation escalators or annual rate reviews in long-term agreements.</li>
              <li><strong>Business expenses:</strong> Your costs (software subscriptions, health insurance, office supplies, etc.) increase with inflation, squeezing margins if you don't adjust pricing.</li>
              <li><strong>Retirement planning:</strong> Money you save today will have less purchasing power in 30-40 years. You need to think in "real" (inflation-adjusted) terms when planning retirement.</li>
              <li><strong>Tax planning:</strong> Inflation pushes nominal income into higher tax brackets even if your real purchasing power stays flat.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Historical Inflation Trends</h3>
            <p className="mb-4">
              U.S. inflation has varied significantly over the decades:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>1960s-1970s:</strong> Moderate inflation in the 1960s (1-3%) accelerated dramatically in the 1970s, peaking above 13% in 1980 due to oil shocks and monetary policy.</li>
              <li><strong>1980s-1990s:</strong> Federal Reserve Chair Paul Volcker's aggressive interest rate hikes tamed inflation, bringing it down to 3-5% by the mid-1980s. The 1990s saw stable, low inflation around 2-3%.</li>
              <li><strong>2000s-2010s:</strong> Inflation remained subdued, averaging 2-2.5% annually. The 2008 financial crisis temporarily caused deflation concerns.</li>
              <li><strong>2020-2024:</strong> Pandemic-related supply chain disruptions and stimulus spending drove inflation to 9.1% in June 2022, the highest since 1981. By 2024, it moderated to 3-4%.</li>
            </ul>
            <p>
              The Federal Reserve's target is <strong>2% inflation</strong>, considered healthy for economic growth while maintaining price stability.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Real vs. Nominal Returns</h3>
            <p className="mb-4">
              Understanding the difference between nominal and real values is critical for financial planning:
            </p>
            <p className="mb-4">
              <strong>Nominal returns</strong> are what you see on your investment statements or hear about in the news. 
              If your portfolio grows from $100,000 to $107,000 in a year, that's a 7% nominal return.
            </p>
            <p className="mb-4">
              <strong>Real returns</strong> adjust for inflation. If inflation was 3% that same year, your real return is 
              approximately 4% (7% - 3%). That $107,000 only buys what $103,883 would have bought last year in today's 
              purchasing power terms.
            </p>
            <p>
              <strong>Formula:</strong> Real Return ≈ Nominal Return - Inflation Rate<br/>
              (The exact formula is: (1 + Nominal) / (1 + Inflation) - 1, but the approximation works well for typical rates.)
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Protecting Yourself From Inflation</h3>
            <ol className="list-decimal pl-6 space-y-2 mb-4">
              <li><strong>Index your rates annually:</strong> Build automatic 3-5% annual rate increases into your pricing strategy and client contracts.</li>
              <li><strong>Invest in inflation-protected assets:</strong> Treasury Inflation-Protected Securities (TIPS), real estate, and stocks historically outpace inflation over long periods.</li>
              <li><strong>Negotiate cost-of-living adjustments:</strong> For retainer agreements or long-term contracts, include annual inflation adjustments tied to CPI.</li>
              <li><strong>Diversify income streams:</strong> Multiple clients and revenue sources protect against inflation in any single market.</li>
              <li><strong>Think in real dollars:</strong> When setting retirement goals, always adjust for inflation. Needing "$80,000/year in retirement" means different things if you're 30 vs. 60.</li>
              <li><strong>Track personal inflation:</strong> CPI measures average consumer spending. Your actual inflation may differ based on geography and lifestyle.</li>
            </ol>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Using This Calculator</h3>
            <p className="mb-4">
              This calculator helps you visualize historical inflation trends and project future values. The chart shows 
              year-over-year CPI changes, letting you see periods of high inflation (like 2021-2022) versus stable periods 
              (2010s). The projection tool estimates what an amount today will need to grow to maintain purchasing power.
            </p>
            <p className="mb-4">
              <strong>Example:</strong> If you want to maintain the purchasing power of $60,000/year in retirement, and you 
              retire in 25 years with average 2.5% inflation, you'll actually need $111,126/year in nominal dollars at that time.
            </p>
            <p>
              Use this tool when negotiating multi-year contracts, setting long-term savings goals, or explaining to clients 
              why your rates need regular increases.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Related Financial Tools</h3>
            <p className="text-gray-700 mb-6 text-base">
              Use these tools together for comprehensive freelance financial planning:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">1099 Tax Calculator</h4>
                <p className="text-sm text-gray-600">
                  Calculate your tax burden after accounting for inflation-adjusted income changes.
                </p>
              </Link>
              
              <Link to="/retirement" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">Retirement Calculator</h4>
                <p className="text-sm text-gray-600">
                  Projects savings in real (inflation-adjusted) dollars so you know actual purchasing power.
                </p>
              </Link>
              
              <Link to="/trends" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">Job Market Trends</h4>
                <p className="text-sm text-gray-600">
                  Industry rate benchmarks help you determine appropriate annual rate increases.
                </p>
              </Link>
              
              <Link to="/calculators" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">All Calculators</h4>
                <p className="text-sm text-gray-600">
                  Access tax, retirement, and inflation tools with educational content for freelancers.
                </p>
              </Link>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-primary-600 p-4 rounded">
            <p className="text-sm text-slate-800">
              <strong>Note:</strong> The chart shows illustrative inflation data for demonstration. For actual CPI data, 
              visit the <a href="https://www.bls.gov/cpi/" className="text-primary-600 underline" target="_blank" rel="noopener noreferrer">U.S. Bureau of Labor Statistics</a>. 
              Past inflation does not predict future inflation rates.
            </p>
          </div>
        </div>
      </section>

      <RecentPosts />
    </main>
  );
}

/* ------- light inputs to match your style ------- */

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  const [raw, setRaw] = useState(String(value));
  React.useEffect(() => {
    const s = String(value);
    if (s !== raw) setRaw(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        className="mt-1 w-full rounded border px-3 py-2"
        value={raw}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
          setRaw(next);
          const n = Number(next);
          if (Number.isFinite(n)) onChange(n);
        }}
        onBlur={() => {
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : value);
          setRaw(String(Number.isFinite(n) ? n : value));
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
    </label>
  );
}

function PercentField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (s: string) => void;
}) {
  const [raw, setRaw] = useState(value);
  React.useEffect(() => setRaw(value), [value]);
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        className="mt-1 w-full rounded border px-3 py-2"
        value={raw}
        onChange={(e) => {
          const v = e.target.value.replace(/[^0-9.]/g, "");
          setRaw(v);
          onChange(v);
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
    </label>
  );
}

function MoneyField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (s: string) => void;
}) {
  const [raw, setRaw] = useState(value);
  React.useEffect(() => setRaw(value), [value]);
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        className="mt-1 w-full rounded border px-3 py-2"
        value={raw}
        onChange={(e) => {
          const v = e.target.value.replace(/[^0-9.]/g, "");
          setRaw(v);
          onChange(v);
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
    </label>
  );
}

function DisplayField({ label, value }: { label: string; value: string }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1 w-full rounded border bg-slate-50 px-3 py-2 text-slate-800">{value}</div>
    </label>
  );
}
